import { ApolloError } from "@apollo/client";
import type {
  ApolloLink,
  FetchResult,
  Operation,
  ServerError,
  ServerParseError,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import type { ErrorResponse } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";
import type { GraphQLError } from "graphql";
import { get, partition } from "lodash";
import type { NavigateFunction } from "react-router";

import { handleGraphQLError, handleSessionError } from "./helpers";

import { networkErrorStorage } from "storage/network-error";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

const BAD_REQUEST = 400;
const FORBIDDEN = 403;

/**
 * Takes over the request when the network error means the session is gone:
 * sends the user to log in again or reloads the page. Returns true when it
 * did.
 */
const resolveNetworkSession = (
  networkError: Error | ServerError | ServerParseError,
): boolean => {
  const { statusCode } = networkError as { statusCode?: number };
  const { result } = networkError as { result?: Record<string, unknown> };
  const errorCode = String(get(result, "errors[0].extensions.code", ""));
  const reloadDelay = 2000;

  /*
   * Only LOGIN_REQUIRED is resolved here (save return URL and redirect to
   * the entry point). Other coded errors such as ACCESS_DENIED fall through
   * so handleGraphQLError can route them.
   */
  if (statusCode === BAD_REQUEST && errorCode === "LOGIN_REQUIRED") {
    void networkErrorStorage.setItem("start_url", window.location.pathname);

    location.assign("/");

    return true;
  }
  if (statusCode === FORBIDDEN) {
    msgError(translate.t("groupAlerts.sessionExpired"));
    setTimeout((): void => {
      location.reload();
    }, reloadDelay);

    return true;
  }

  return false;
};

const notifyNetworkError = (
  networkError: Error | ServerError | ServerParseError,
  operation: Operation,
): void => {
  const { statusCode } = networkError as { statusCode?: number };
  const notAllowed = 405;
  const requestConflict = 409;
  const tooManyRequests = 429;
  const gatewayTimeout = 504;

  switch (statusCode) {
    case undefined:
      msgError(translate.t("groupAlerts.errorNetwork"), "Offline");
      break;
    case BAD_REQUEST:
      break;
    case notAllowed:
      msgError(
        translate.t("groupAlerts.methodNotAllowed"),
        "Method Not Allowed",
      );
      break;
    case requestConflict:
      msgError(translate.t("groupAlerts.requestConflict"), "Request Conflict");
      break;
    case tooManyRequests:
      msgError(translate.t("groupAlerts.tooManyRequests"), "Too Many Requests");
      Logger.warning("Too Many Requests", { ...networkError });
      break;
    case gatewayTimeout:
      msgError(translate.t("groupAlerts.gatewayTimeout"));
      Logger.warning("Gateway Timeout", {
        ...networkError,
        operationName: operation.operationName,
      });
      break;
    default:
      msgError(translate.t("groupAlerts.errorTextsad"));
      Logger.warning("A network error occurred", { ...networkError });
  }
};

const handleNetworkError = (
  networkError: Error | ServerError | ServerParseError,
  operation: Operation,
): boolean => {
  if (resolveNetworkSession(networkError)) return true;
  notifyNetworkError(networkError, operation);

  return false;
};

const HANDLED_ERROR_MESSAGE = "Request resolved by the global error handler";

const failWith = (apolloError: ApolloError): Observable<FetchResult> =>
  new Observable((observer): void => {
    observer.error(apolloError);
  });

const isClaimed = (
  error: GraphQLError,
  handledErrors: readonly string[],
): boolean => {
  const code = String(get(error, "extensions.code", ""));

  return handledErrors.some(
    (handled): boolean => handled === code || handled === error.message,
  );
};

/**
 * What the shared handler does with the errors a view did not claim; a
 * resolver returns true when it took over the request. The notifying set
 * routes access errors and shows notices; the quiet set, for requests the
 * view can do without, only ends a lost session.
 */
interface IErrorResponders {
  notifyUnhandled: () => void;
  resolveApiError: (error: GraphQLError) => boolean;
  resolveNetworkError: (
    networkError: Error | ServerError | ServerParseError,
    operation: Operation,
  ) => boolean;
}

const notifyingResponders = (navigate: NavigateFunction): IErrorResponders => ({
  notifyUnhandled: (): void => {
    msgError(translate.t("groupAlerts.errorTextsad"));
  },
  resolveApiError: (error): boolean => handleGraphQLError(error, navigate),
  resolveNetworkError: handleNetworkError,
});

const quietResponders: IErrorResponders = {
  notifyUnhandled: (): void => undefined,
  resolveApiError: handleSessionError,
  resolveNetworkError: resolveNetworkSession,
};

const isQuery = ({ query }: Operation): boolean =>
  query.definitions.some(
    (definition): boolean =>
      definition.kind === Kind.OPERATION_DEFINITION &&
      definition.operation === OperationTypeNode.QUERY,
  );

const handleDeclaredErrors = (
  { graphQLErrors, networkError, operation }: ErrorResponse,
  handledErrors: readonly string[],
  responders: IErrorResponders,
): Observable<FetchResult> | undefined => {
  const errors =
    graphQLErrors && graphQLErrors.length > 0
      ? graphQLErrors
      : (get(networkError, "result.errors", []) as readonly GraphQLError[]);
  const handled = new ApolloError({ errorMessage: HANDLED_ERROR_MESSAGE });

  if (networkError && responders.resolveNetworkError(networkError, operation)) {
    return failWith(handled);
  }

  const [claimed, unclaimed] = partition(errors, (error): boolean =>
    isClaimed(error, handledErrors),
  );

  if (unclaimed.some(responders.resolveApiError)) {
    return failWith(handled);
  }

  if (unclaimed.length > 0) {
    Logger.error("An unhandled API error occurred", {
      errors: unclaimed,
      operationName: operation.operationName,
    });
    const notifiedByNetwork =
      networkError !== undefined &&
      get(networkError, "statusCode") !== BAD_REQUEST;
    if (claimed.length === 0 && !notifiedByNetwork) {
      responders.notifyUnhandled();
    }
  }

  if (!networkError) return undefined;

  if (errors.length === 0) {
    Logger.error("A network error occurred", {
      message: networkError.message,
      operationName: operation.operationName,
      statusCode: get(networkError, "statusCode"),
    });
  }

  if (claimed.length > 0) {
    return failWith(new ApolloError({ graphQLErrors: claimed }));
  }

  return failWith(
    unclaimed.length > 0 ? handled : new ApolloError({ networkError }),
  );
};

// Global error handler
const errorLink = (navigate: NavigateFunction): ApolloLink => {
  return onError((error): Observable<FetchResult> | undefined => {
    const { graphQLErrors, networkError, operation } = error;
    const { handledErrors, nonBlocking, skipGlobalErrorHandler } =
      operation.getContext();
    if (skipGlobalErrorHandler === true) return undefined;
    const quiet = nonBlocking === true && isQuery(operation);
    if (handledErrors !== undefined || quiet) {
      return handleDeclaredErrors(
        error,
        handledErrors ?? [],
        quiet ? quietResponders : notifyingResponders(navigate),
      );
    }

    const effectiveGraphQLErrors =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors
        : (get(networkError, "result.errors", []) as readonly GraphQLError[]);

    /*
     * Network-level routing (session redirects/reloads) runs first and, when
     * it performs a terminal action, takes precedence over GraphQL handling so
     * an auth error returned as HTTP 400 does not trigger conflicting actions.
     */
    if (networkError && handleNetworkError(networkError, operation)) {
      return failWith(new ApolloError({ errorMessage: HANDLED_ERROR_MESSAGE }));
    }

    if (effectiveGraphQLErrors.length > 0) {
      const handled = effectiveGraphQLErrors.some((gqlError): boolean =>
        handleGraphQLError(gqlError, navigate),
      );

      if (handled) {
        return failWith(
          new ApolloError({ errorMessage: HANDLED_ERROR_MESSAGE }),
        );
      }
    }

    if (networkError) {
      /*
       * Forward unhandled graphQLErrors (e.g. business errors returned as
       * HTTP 400) so component onError handlers can react to them. Responses
       * without GraphQL errors forward the networkError, which would otherwise
       * be dropped and leave the error with no message.
       */
      return failWith(
        effectiveGraphQLErrors.length > 0
          ? new ApolloError({ graphQLErrors: effectiveGraphQLErrors })
          : new ApolloError({ networkError }),
      );
    }

    return undefined;
  });
};

export { errorLink };
